import { useEffect, useState, useRef, RefObject } from 'react';

interface ContainerSize {
  width: number;
  height: number;
}

/**
 * A hook to track the size of a container element
 * 
 * @returns [ref, size] - A ref to attach to the container and the current size
 */
export function useContainerSize<T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T>,
  ContainerSize
] {
  const containerRef = useRef<T>(null);
  const [containerSize, setContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });
  
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
      }
    };

    // Initial size calculation
    updateSize();
    
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(containerRef.current);
    } else {
      window.addEventListener('resize', updateSize);
    }
    
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateSize);
      }
    };
  }, []);

  return [containerRef, containerSize];
}